import React, {useEffect, useState} from "react";
import {Table, Select, DatePicker, Button, Image, Row, Col, Empty, Input} from "antd";
import {useFetch} from "../../hooks/useFetch";
import {useStore} from "../../hooks/useStore";
import moment from "moment";
import {Link} from "react-router-dom";
import {useObserver, observer} from "mobx-react-lite";
import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";
import {FileSearchOutlined, ExclamationCircleOutlined} from "@ant-design/icons";
import locale from "antd/es/date-picker/locale/tr_TR";
import {Box} from "../customer/customerAccountingPage/customerAccountingRow/CustomerAccountingRow.styles";
import UseCreditIcon from "../../public/images/use-credit-icon.svg";
import LoadCreditIcon from "../../public/images/load-credit-icon.svg";
import {ConditionalTooltip} from "../_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {truncateString} from "../../utils/helpers";
import {ObjectionModal} from "./modals/ObjectionModal";
import {EmptyMessage} from "../_defaultComponents/EmptyMessage/EmptyMessage";
import {TableWrapper} from "../_defaultComponents/TableWrapper/TableWrapper";
import {
    MainContainer,
    LeftSection,
    HeaderContainer,
    BoxesContainer,
    DateRangeContainer,
    TransactionTypeContainer,
    Line,
    SubtypesMobileContainer,
    SubtypeMobileItem,
    SubtypeMobileRow,
    CashIconWrapper
} from "./CustomerCreditLogTable.styles";
import {CustomerOrderDetailModal} from "components/customer/customerOrdersPage/customerOrdersTable/customerOrderDetailModal/CustomerOrderDetailModal";
import HourFilter from "components/_defaultComponents/FilterComponents/HourFilter";
import {
    creditLogSubTypeOptions,
    creditLogTransactionTypeOptions,
    creditLogTransactionTypes
} from "constants/constants";
import {TransactionTypeFilter} from "components/_defaultComponents/FilterComponents";
import {FaTurkishLiraSign} from "react-icons/fa6";

export const CustomerCreditLogTable = observer(
    ({creditStats, selectedDateRange, setCreditStats, setSelectedDateRange, defaultDateRange}) => {
        const stores = useStore();
        const [selectedTransactionType, setSelectedTransactionType] = useState([]);
        const [selectedSubType, setSelectedSubType] = useState("all");
        const [selectedHasWalletTransaction, setSelectedHasWalletTransaction] = useState("all");
        const [pageParam, setPageParam] = useState(1);
        const [updateFileUpdateVisibilty, setFileUpdateVisibility] = useState(false);
        const [orderId, setOrderId] = useState(null);
        const [deliveryId, setDeliveryId] = useState(null);
        const [refreshTrigger, setRefreshTrigger] = useState(0);
        const [selectedEndTime, setSelectedEndTime] = useState(
            stores?.subcompanyUserStore?.customerEndTime || "06:00"
        );

        useEffect(() => {
            const storeEndTime = stores?.subcompanyUserStore?.customerEndTime;
            if (storeEndTime && storeEndTime !== selectedEndTime) {
                setSelectedEndTime(storeEndTime);
            }
        }, [stores?.subcompanyUserStore?.customerEndTime, selectedEndTime]);
        const [customerOrderDetailModalVisible, setCustomerOrderDetailModalVisible] = useState(
            false
        );
        const [deliveryIdForCustomerOrderDetail, setDeliveryIdForCustomerOrderDetail] = useState(
            ""
        );
        // Header'daki seçili alt firmayı kullan
        const subCompanyId = stores.meStore.selectedSubCompanyId;

        const [paymentTransactionLogsLoading, paymentTransactionLogs, _, __, refetch] = useFetch({
            defaultDataValue: [],
            fetchFn: stores.companyStore.getSubCompanyPaymentTransactionLogForClient.bind(null, {
                subCompanyId,
                skip: pageParam - 1,
                limit: 10,
                startDate:
                    selectedDateRange?.length === 2 && selectedDateRange[0] && selectedDateRange[1]
                        ? moment(selectedDateRange[0]).format("YYYY-MM-DD")
                        : undefined,
                endDate:
                    selectedDateRange?.length === 2 && selectedDateRange[1]
                        ? moment(selectedDateRange[1]).format("YYYY-MM-DD")
                        : undefined,
                transactionType: selectedTransactionType?.length
                    ? selectedTransactionType
                    : undefined,
                subType: selectedSubType === "all" ? undefined : selectedSubType,
                hasWalletTransaction:
                    selectedHasWalletTransaction === "all"
                        ? undefined
                        : selectedHasWalletTransaction === "true",
                endTime: selectedEndTime || "06:00"
            }),
            initialCall: false,
            showLoading: true,
            dependencies: [
                subCompanyId,
                selectedTransactionType,
                selectedSubType,
                selectedHasWalletTransaction,
                selectedDateRange,
                pageParam,
                selectedEndTime
            ]
        });

        const [] = useFetch({
            defaultDataValue: {},
            fetchFn: stores.userStore.getUserCreditStats.bind(null, {
                subCompanyId,
                ...(selectedDateRange?.length === 2 && {
                    startDate: moment(selectedDateRange?.[0]).format("YYYY-MM-DD"),
                    endDate: moment(selectedDateRange?.[1]).format("YYYY-MM-DD")
                })
            }),
            dependencies: [selectedDateRange, subCompanyId],
            initialCall: true,
            callbackFunc: result => {
                setCreditStats && setCreditStats(result);
            }
        });

        const handleSelectedTransactionType = value => {
            setSelectedTransactionType(value ?? []);
            setPageParam(1);
        };

        const closeModals = () => {
            setFileUpdateVisibility(false);
        };

        const handleObjectionModalClose = isSubmitted => {
            closeModals();
            if (isSubmitted) {
                setRefreshTrigger(prev => prev + 1);
            }
        };

        const handleObjection = (orderId, deliveryId) => {
            setOrderId(orderId);
            setDeliveryId(deliveryId);
            setFileUpdateVisibility(true);
        };

        const handleTimeChange = endTime => {
            setSelectedEndTime(endTime);
            if (endTime) {
                stores.subcompanyUserStore.setCustomerEndTime(endTime);
            }
        };

        useEffect(() => {
            refetch();
        }, []);

        useEffect(() => {
            refetch();
        }, [refreshTrigger]);

        // Artık header'daki seçimi kullandığımız için bu useEffect'e gerek yok

        const renderLoadedCredit = () => {
            if (creditStats === null) {
                return "###";
            }

            return `${creditStats?.data?.data?.stats?.totalCreditLoad?.toLocaleString("tr-TR", {
                maximumFractionDigits: 1
            }) || "-"} ₺`;
        };

        const renderUsedCredit = () => {
            if (creditStats === null) {
                return "###";
            }

            return `${creditStats?.data?.data?.stats?.totalCreditUse?.toLocaleString("tr-TR", {
                maximumFractionDigits: 1
            }) || "-"} ₺`;
        };

        const totalCodCashAmount = creditStats?.data?.data?.stats?.totalCodCashAmount;

        const renderCashCollection = () =>
            `${totalCodCashAmount.toLocaleString("tr-TR", {
                maximumFractionDigits: 1
            })} ₺`;

        return useObserver(() => (
            <div style={{width: "100%"}}>
                <Line />
                <div style={{width: "100%"}}>
                    <div>
                        <MainContainer>
                            <LeftSection>
                                <HeaderContainer>
                                    <DateRangeContainer>
                                        <span>Başlangıç - Bitiş Tarihi</span>
                                        <DatePicker.RangePicker
                                            placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
                                            onChange={value => {
                                                setSelectedDateRange(value);
                                            }}
                                            value={selectedDateRange}
                                            locale={locale}
                                            allowClear
                                        />
                                    </DateRangeContainer>
                                    <DateRangeContainer>
                                        <span>Kapanış Saati</span>
                                        <HourFilter onTimeChange={handleTimeChange} />
                                    </DateRangeContainer>
                                    <TransactionTypeContainer>
                                        <TransactionTypeFilter
                                            value={selectedTransactionType}
                                            onChange={handleSelectedTransactionType}
                                            showAllOption={false}
                                            mode="multiple"
                                            placeholder="İşlem Türü"
                                            allowClear
                                        />
                                    </TransactionTypeContainer>
                                </HeaderContainer>
                                <BoxesContainer>
                                    <Box>
                                        <img src={LoadCreditIcon} alt="load credit icon" />
                                        <b>Toplam Yüklenen</b>
                                        <b>{renderLoadedCredit()}</b>
                                    </Box>
                                    <Box>
                                        <img src={UseCreditIcon} alt="use credit icon" />
                                        <b>Toplam Harcanan</b>
                                        <b>{renderUsedCredit()}</b>
                                    </Box>
                                    {totalCodCashAmount != null && (
                                        <Box>
                                            <CashIconWrapper>
                                                <span>
                                                    <FaTurkishLiraSign />
                                                </span>
                                            </CashIconWrapper>
                                            <b>Toplam Nakit Tahsilat</b>
                                            <b>{renderCashCollection()}</b>
                                        </Box>
                                    )}
                                    {/* {selectedDateRange?.length === 2 && (
                                <Fragment>
                                    <Box>
                                        <img src={LoadCreditIcon} alt="use credit icon" />
                                        <b>Başlangıç Bakiyesi</b>
                                        <b>{startingBalance()}</b>
                                    </Box>
                                    <Box>
                                        <img src={UseCreditIcon} alt="use credit icon" />
                                        <b>Bitiş Bakiyesi</b>
                                        <b>{endingBalance()}</b>
                                    </Box>
                                </Fragment>
                            )} */}
                                </BoxesContainer>
                            </LeftSection>
                        </MainContainer>

                        <div style={{marginTop: "20px"}}>
                            <TableWrapper
                                rowKey={(record, index) => record._id || index.toString()}
                                dataSource={paymentTransactionLogs?.data?.rows}
                                loading={paymentTransactionLogsLoading}
                                scroll={{x: "max-content"}}
                                locale={{
                                    emptyText: <EmptyMessage />
                                }}
                                pagination={{
                                    total: paymentTransactionLogs?.data?.dataCount,
                                    current: pageParam,
                                    hideOnSinglePage: true,
                                    showSizeChanger: false,
                                    onChange: page => {
                                        setPageParam(page);
                                    }
                                }}
                                expandable={{
                                    expandedRowRender: record => {
                                        const isMobile =
                                            typeof window !== "undefined" &&
                                            window.innerWidth <= 768;

                                        return (
                                            <>
                                                {record.subTypes && record.subTypes.length > 0 ? (
                                                    isMobile ? (
                                                        <SubtypesMobileContainer>
                                                            {record.subTypes.map(
                                                                (subType, index) => (
                                                                    <SubtypeMobileItem key={index}>
                                                                        <SubtypeMobileRow>
                                                                            <strong>
                                                                                İşlem Türü:
                                                                            </strong>
                                                                            <span>
                                                                                {creditLogSubTypeOptions.find(
                                                                                    option =>
                                                                                        option.value ===
                                                                                        subType.subType
                                                                                )?.label ||
                                                                                    subType.subType ||
                                                                                    "-"}
                                                                            </span>
                                                                        </SubtypeMobileRow>
                                                                        <SubtypeMobileRow>
                                                                            <strong>Fiyat:</strong>
                                                                            <span
                                                                                style={{
                                                                                    color:
                                                                                        subType.amount >
                                                                                        0
                                                                                            ? "green"
                                                                                            : "red"
                                                                                }}
                                                                            >
                                                                                {subType.amount} TL
                                                                            </span>
                                                                        </SubtypeMobileRow>
                                                                    </SubtypeMobileItem>
                                                                )
                                                            )}
                                                        </SubtypesMobileContainer>
                                                    ) : (
                                                        <div className="subtypes-container">
                                                            <Table
                                                                dataSource={record.subTypes}
                                                                pagination={false}
                                                                showHeader={false}
                                                                size="small"
                                                                rowClassName={(record, index) => {
                                                                    return "gray-background";
                                                                }}
                                                            >
                                                                <Table.Column width={200} />
                                                                <Table.Column width={200} />
                                                                <Table.Column width={200} />
                                                                <Table.Column width={150} />
                                                                <Table.Column
                                                                    align="center"
                                                                    key="subType"
                                                                    width={200}
                                                                    dataIndex="subType"
                                                                    render={subType => (
                                                                        <div className="subtype-cell">
                                                                            {creditLogSubTypeOptions.find(
                                                                                option =>
                                                                                    option.value ===
                                                                                    subType
                                                                            )?.label ||
                                                                                subType ||
                                                                                "-"}
                                                                        </div>
                                                                    )}
                                                                />
                                                                <Table.Column
                                                                    key="amount"
                                                                    align="center"
                                                                    width={150}
                                                                    dataIndex="amount"
                                                                    render={amount => (
                                                                        <div
                                                                            className="amount-cell"
                                                                            style={{
                                                                                color:
                                                                                    amount > 0
                                                                                        ? "green"
                                                                                        : "red"
                                                                            }}
                                                                        >
                                                                            {amount} TL
                                                                        </div>
                                                                    )}
                                                                />
                                                                <Table.Column width={150} />
                                                                <Table.Column width={150} />
                                                            </Table>
                                                        </div>
                                                    )
                                                ) : record.transactionPhoto ? (
                                                    <div>
                                                        <h2>Fatura Görüntüsü </h2>
                                                        {record.transactionPhoto.includes(".pdf") ||
                                                        record.transactionPhoto.includes(
                                                            "buluttahsilat"
                                                        ) ? (
                                                            <a
                                                                href={record.transactionPhoto}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                <Button type="primary">
                                                                    Dekont Görüntüle
                                                                </Button>
                                                            </a>
                                                        ) : (
                                                            <Image
                                                                src={record.transactionPhoto}
                                                                width={200}
                                                                height={200}
                                                                style={{
                                                                    objectFit: "contain"
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                ) : (
                                                    "Fatura Görüntüsü Bulunamadı"
                                                )}
                                            </>
                                        );
                                    },
                                    rowExpandable: record =>
                                        (record.subTypes && record.subTypes.length > 0) ||
                                        (record?.subType === "PAYMENT" &&
                                            record?.transactionType === "LOAD")
                                }}
                            >
                                <Table.Column
                                    dataIndex="createdAt"
                                    title={"Tarih"}
                                    width={200}
                                    render={date => (
                                        <span>{moment(date).format("DD.MM.YYYY HH:mm")}</span>
                                    )}
                                />
                                <Table.Column
                                    dataIndex="vigoOrderNumber"
                                    width={200}
                                    title={"Sipariş Numarası"}
                                    render={(_, record) => (
                                        <span>
                                            {record?.order !== null ? (
                                                record?.order?.vigoOrderNumber ? (
                                                    <span
                                                        style={{
                                                            color: "#1890ff",
                                                            cursor: "pointer",
                                                            wordBreak: "break-all"
                                                        }}
                                                        onClick={() => {
                                                            setDeliveryIdForCustomerOrderDetail(
                                                                record?.deliveryId
                                                            );
                                                            setCustomerOrderDetailModalVisible(
                                                                true
                                                            );
                                                        }}
                                                    >
                                                        {record?.order?.vigoOrderNumber}
                                                    </span>
                                                ) : (
                                                    <ConditionalTooltip
                                                        condition={
                                                            record?.order?.orderNumber
                                                                ? truncateString(
                                                                      record?.order?.orderNumber,
                                                                      16
                                                                  ).indexOf("...") > 0
                                                                : false
                                                        }
                                                        text={record?.order?.orderNumber}
                                                        child={
                                                            record?.deliveryId &&
                                                            record?.order?.orderNumber ? (
                                                                <span
                                                                    style={{
                                                                        color: "#1890ff",
                                                                        cursor: "pointer",
                                                                        textDecoration: "underline",
                                                                        wordBreak: "break-all"
                                                                    }}
                                                                    onClick={() => {
                                                                        setDeliveryIdForCustomerOrderDetail(
                                                                            record?.deliveryId
                                                                        );
                                                                        setCustomerOrderDetailModalVisible(
                                                                            true
                                                                        );
                                                                    }}
                                                                >
                                                                    {record?.order?.orderNumber
                                                                        ? truncateString(
                                                                              record?.order
                                                                                  ?.orderNumber,
                                                                              16
                                                                          )
                                                                        : "-"}
                                                                </span>
                                                            ) : (
                                                                "-"
                                                            )
                                                        }
                                                    />
                                                )
                                            ) : (
                                                <ConditionalTooltip
                                                    condition={
                                                        record?.order?.orderNumber
                                                            ? truncateString(
                                                                  record?.order?.orderNumber,
                                                                  16
                                                              ).indexOf("...") > 0
                                                            : false
                                                    }
                                                    text={record?.order?.orderNumber}
                                                    child={
                                                        record?.deliveryId &&
                                                        record?.order?.orderNumber ? (
                                                            <span
                                                                style={{
                                                                    color: "#1890ff",
                                                                    cursor: "pointer",
                                                                    wordBreak: "break-all"
                                                                }}
                                                                onClick={() => {
                                                                    setDeliveryIdForCustomerOrderDetail(
                                                                        record?.deliveryId
                                                                    );
                                                                    setCustomerOrderDetailModalVisible(
                                                                        true
                                                                    );
                                                                }}
                                                            >
                                                                {record?.order?.orderNumber
                                                                    ? truncateString(
                                                                          record?.order
                                                                              ?.orderNumber,
                                                                          16
                                                                      )
                                                                    : "-"}
                                                            </span>
                                                        ) : (
                                                            "-"
                                                        )
                                                    }
                                                />
                                            )}
                                        </span>
                                    )}
                                />
                                {!subCompanyId && (
                                    <Table.Column
                                        key="subcompanyId"
                                        dataIndex="subcompanyId"
                                        width={150}
                                        title={"Detay"}
                                        render={subcompanyId => (
                                            <CustomButton
                                                href={
                                                    "/panel/companies/sub-company/" + subcompanyId
                                                }
                                                data-testid="subcompanyDetail"
                                                onClick={event => {
                                                    event.preventDefault();
                                                    window.location.href =
                                                        "/panel/companies/sub-company/" +
                                                        subcompanyId;
                                                }}
                                                icon={<FileSearchOutlined />}
                                            />
                                        )}
                                    />
                                )}
                                <Table.Column
                                    key="courier"
                                    dataIndex="courier"
                                    width={200}
                                    title={"Kurye"}
                                    render={courier => (
                                        <span style={{wordBreak: "break-word"}}>
                                            {courier ? (
                                                stores.meStore.checkRole(
                                                    "notVisibleForCompanyUser"
                                                ) ? (
                                                    <Link
                                                        target="_blank"
                                                        to={{
                                                            pathname: "/couriers/" + courier?.id
                                                        }}
                                                    >
                                                        {courier?.fullname}
                                                    </Link>
                                                ) : (
                                                    courier?.fullname ?? "Kurye"
                                                )
                                            ) : (
                                                "-"
                                            )}
                                        </span>
                                    )}
                                />
                                <Table.Column
                                    dataIndex="receiverName"
                                    title={"Alıcı Adı/ Fiş Numarası"}
                                    width={150}
                                    render={(receiverName, record) => (
                                        <span style={{wordBreak: "break-word"}}>
                                            {receiverName || record?.order?.receiptNumber || "-"}
                                        </span>
                                    )}
                                />
                                <Table.Column
                                    dataIndex="transactionType"
                                    title={"İşlem Türü"}
                                    render={transactionType => (
                                        <div
                                            style={{
                                                display: "inline-block",
                                                backgroundColor: creditLogTransactionTypeOptions.find(
                                                    option => option.value === transactionType
                                                )?.color,
                                                color: creditLogTransactionTypeOptions.find(
                                                    option => option.value === transactionType
                                                )?.textColor,
                                                padding: "5px",
                                                borderRadius: "5px"
                                            }}
                                        >
                                            {creditLogTransactionTypeOptions.find(
                                                option => option.value === transactionType
                                            )?.label || transactionType}
                                        </div>
                                    )}
                                />
                                <Table.Column
                                    dataIndex="clientAmount"
                                    title={"Fiyat"}
                                    width={150}
                                    render={clientAmount => (
                                        <span>
                                            {clientAmount
                                                ? `${clientAmount?.toLocaleString("tr-TR", {
                                                      maximumFractionDigits: 1
                                                  })} TL`
                                                : "0 TL"}
                                        </span>
                                    )}
                                />
                                <Table.Column
                                    dataIndex="description"
                                    width={150}
                                    title={"Açıklama"}
                                    render={description => (
                                        <span style={{wordBreak: "break-word"}}>
                                            {description ? `${description}` : "-"}
                                        </span>
                                    )}
                                />
                                {!(
                                    selectedTransactionType?.length === 1 &&
                                    selectedTransactionType[0] === creditLogTransactionTypes.LOAD
                                ) && (
                                    <Table.Column
                                        dataIndex="transactionType"
                                        title={"İtiraz Bildir"}
                                        width={150}
                                        render={(_, record) => {
                                            if (
                                                record.transactionType === "USE" &&
                                                !record.paymentObjection
                                            ) {
                                                return (
                                                    <CustomButton
                                                        onClick={() =>
                                                            handleObjection(
                                                                record.order?._id,
                                                                record.deliveryId
                                                            )
                                                        }
                                                        style={{borderRadius: "10px"}}
                                                    >
                                                        İtiraz Bildir <ExclamationCircleOutlined />
                                                    </CustomButton>
                                                );
                                            }
                                            if (record.paymentObjection) {
                                                const {status} = record.paymentObjection;

                                                if (status === "pending") {
                                                    return (
                                                        <CustomButton
                                                            style={{
                                                                backgroundColor: "#ff9f43",
                                                                color: "white",
                                                                borderRadius: "10px"
                                                            }}
                                                            disabled
                                                        >
                                                            İnceleniyor
                                                        </CustomButton>
                                                    );
                                                }

                                                if (status === "approved") {
                                                    return (
                                                        <CustomButton
                                                            style={{
                                                                backgroundColor: "#00b894",
                                                                color: "white",
                                                                borderRadius: "10px"
                                                            }}
                                                            disabled
                                                        >
                                                            Onaylandı
                                                        </CustomButton>
                                                    );
                                                }

                                                if (status === "rejected") {
                                                    return (
                                                        <CustomButton
                                                            style={{
                                                                backgroundColor: "#ff5354",
                                                                color: "white",
                                                                borderRadius: "10px"
                                                            }}
                                                            disabled
                                                        >
                                                            Reddedildi
                                                        </CustomButton>
                                                    );
                                                }
                                            }
                                            return null;
                                        }}
                                    />
                                )}
                            </TableWrapper>
                        </div>
                        <ObjectionModal
                            subCompanyId={subCompanyId}
                            deliveryId={deliveryId}
                            orderId={orderId}
                            visibility={updateFileUpdateVisibilty}
                            onCancel={handleObjectionModalClose}
                        />
                        <CustomerOrderDetailModal
                            visible={customerOrderDetailModalVisible}
                            onCancel={() => {
                                setCustomerOrderDetailModalVisible(false);
                            }}
                            deliveryId={deliveryIdForCustomerOrderDetail}
                        />
                    </div>
                </div>
            </div>
        ));
    }
);
